import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import Page from '../../components/Page';
import {
  getDomain,
  fetchITicketWidgets,
  parseITicketUserData,
} from '../../utils/functions';
import ImageAccordion from '../../components/ImageAccordion';
import { fetchBenefits, fetchCustomerData } from '../../utils/functions';
import Spinner from '../../components/Spinner';
import { Benefit, iTicketWidget } from '../../types/drupal';
import Button from '../../components/Button';
import '../../styles/ITicketWidgetStyles.css';

interface Props {
  idToken: string;
}

function BenefitsPage(props: Props): JSX.Element {
  const domain = getDomain();
  const pageId: { [key: string]: string } = {};
  pageId['ruka'] = process.env.REACT_APP_BENEFITS_PAGE_RUKA ?? '';
  pageId['pyha'] = process.env.REACT_APP_BENEFITS_PAGE_PYHA ?? '';

  const [benefits, setBenefits] = useState<Benefit[]>();
  const [errorMessage, setErrorMessage] = useState<string | null>();

  const widgetContainer = useRef<HTMLUListElement>(null);
  const [iTicketWidgets, setITicketWidgets] = useState<iTicketWidget[] | null>(
    []
  );
  const [fetchingITicketData, setFetchingIticketData] = useState(true);

  const [iTicketUserParams, setITicketUserParams] = useState<string>();

  const { i18n, t } = useTranslation('benefits');
  const currentLanguage = i18n.language;

  useEffect(() => {
    async function getBenefits() {
      const data = await fetchBenefits(currentLanguage, props.idToken);

      if (data) {
        setBenefits(data);
      } else {
        setErrorMessage(t('No benefits to display.'));
      }
    }
    if (props.idToken) {
      getBenefits();
    }
  }, [currentLanguage, props.idToken]);

  function sortBenefits(a: Benefit, b: Benefit) {
    // Sort benefits by taxonomy term weight.
    if (a.weight > b.weight) {
      return 1;
    }
    return -1;
  }

  function formatDate(date: string) {
    const formattedDate = new Date(date).toLocaleDateString('fi-FI', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
    return formattedDate;
  }

  function validateBenefits(benefits: Benefit[]) {
    const currentDate = new Date();

    // Validate benefits based on the end date if the benefit has one.
    const validatedBenefits = benefits.filter((benefit) => {
      if (Object.keys(benefit.dates).length === 0) {
        // Benefits without a set start and end date are always valid.
        return true;
      }
      const benefitEndDate = new Date(benefit.dates.end);
      const benefitStartDate = new Date(benefit.dates.start);

      if (benefitStartDate <= currentDate && benefitEndDate >= currentDate) {
        return true;
      }
      return false;
    });

    return validatedBenefits;
  }

  function filterUpcomingBenefits(benefits: Benefit[]) {
    const currentDate = new Date();

    const upcomingBenefits = benefits.filter((benefit) => {
      const benefitStartDate = new Date(benefit.dates.start);

      if (benefitStartDate) {
        if (benefitStartDate > currentDate) {
          return true;
        }
        return false;
      }
    });

    return upcomingBenefits;
  }

  useEffect(() => {
    if (iTicketWidgets && iTicketWidgets.length > 0) {
      loadWidgets(0);
    }
  }, [iTicketWidgets]);

  // Function for loading in the iTicket widgets.
  function loadWidgets(i = 0) {
    if (!iTicketWidgets || !iTicketWidgets[i]) {
      return;
    }

    const widget = iTicketWidgets[i];

    let widgetUrl = widget.widget[currentLanguage];
    // If user has previously used iticket, the code and username will be added to the
    // widget src, and the user will be automatically logged in.
    if (iTicketUserParams) {
      widgetUrl += `&${iTicketUserParams}`;
    }

    // Generate the script.
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = widgetUrl;

    if (!widgetContainer.current) {
      return;
    }

    const widgetPlaceholder = widgetContainer.current.querySelector(
      `#iticket-widget-placeholder-${i}`
    );
    if (!widgetPlaceholder) {
      return;
    }

    // Temporarily set the actual id for the placeholder
    // element that the iTicket script will target.
    const originalId = widgetPlaceholder.id;
    widgetPlaceholder.id = widget.token;

    script.onload = () => {
      if (!widgetContainer.current) {
        return;
      }

      const loadedWidget = widgetContainer.current.querySelector(
        `[id="${widget.token}"]`
      );
      if (!loadedWidget) {
        return;
      }

      // Set back the placeholder id, so we can load the next widget
      // if available. If there are multiple elements with the same id,
      // or loading multiple iTicket widgets won't work.
      loadedWidget.id = originalId;

      // If there are more widgets available, call this function again.
      if (iTicketWidgets.length >= i + 1) {
        loadWidgets(i + 1);
      }
    };
    document.body.appendChild(script);
  }

  // Fetch iTicket widget data and customer info from cdp.
  useEffect(() => {
    async function getWidgetsAndCustomer() {
      const customer = await fetchCustomerData(props.idToken, false, 'cdp');

      if (customer && customer.cdpData) {
        const userDataParams = parseITicketUserData(
          customer.cdpData.references
        );

        if (userDataParams) {
          setITicketUserParams(userDataParams);
        }
      }

      const data = await fetchITicketWidgets(
        null,
        null,
        null,
        'true',
        props.idToken
      );

      if (data) {
        setITicketWidgets(data);
      }

      setFetchingIticketData(false);
    }

    if (props.idToken) {
      getWidgetsAndCustomer();
    }
  }, [props.idToken]);

  let mainContent = <Spinner />;
  if (!fetchingITicketData && iTicketWidgets && iTicketWidgets.length > 0) {
    mainContent = (
      <ul ref={widgetContainer} className='iticket-widget-container'>
        {iTicketWidgets.map((widget, i) => (
          <li key={i}>
            <ImageAccordion
              defaultIsOpen={i === 0 ? true : false}
              title={
                <div>
                  <h3 className='text-2xl md:mt-4'>
                    {widget.title[currentLanguage]}
                  </h3>

                  {widget.description &&
                    widget.description[currentLanguage] && (
                      <div
                        className={`text-lg widget-description-${domain}`}
                        dangerouslySetInnerHTML={{
                          __html: widget.description[currentLanguage],
                        }}
                      ></div>
                    )}

                  {widget.deadline && (
                    <p className='font-rukaNeue text-xl'>
                      {t('Valid until')}{' '}
                      {dayjs(widget.deadline).format('DD.MM.YY')}
                    </p>
                  )}
                </div>
              }
            >
              <div>
                {/* Set a placeholder id for the iTicket widget */}
                <div id={`iticket-widget-placeholder-${i}`}></div>
              </div>
            </ImageAccordion>
          </li>
        ))}
      </ul>
    );
  } else if (!fetchingITicketData) {
    mainContent = <div></div>;
  }

  const validatedBenefits = benefits ? validateBenefits(benefits) : [];
  const upcomingBenefits = benefits ? filterUpcomingBenefits(benefits) : [];

  let secondaryContent = <Spinner />;

  if (validatedBenefits) {
    secondaryContent = (
      <>
        <ul>
          {validatedBenefits.sort(sortBenefits).map((benefit, i) => (
            <li key={i}>
              <ImageAccordion
                title={
                  <div className='flex flex-col'>
                    <h3 className='text-2xl md:mt-4'>{benefit.name}</h3>
                    {benefit.dates.end && (
                      <p
                        className='font-rukaNeue text-xl'
                        dangerouslySetInnerHTML={{
                          __html: `${t('Valid until')} ${formatDate(
                            benefit.dates.end
                          )}`,
                        }}
                      ></p>
                    )}
                    <p className='font-rukaNeue text-xl'>{benefit.subtitle}</p>
                  </div>
                }
                image={benefit.image}
              >
                <div className='my-4'>
                  <h3>{benefit.title}</h3>
                  <p
                    className='my-4'
                    dangerouslySetInnerHTML={{ __html: benefit.body }}
                  ></p>
                  {benefit.cta.url && (
                    <Button
                      className='w-full mt-8 md:mx-auto md:w-fit md:px-4 md:min-w-[20rem]'
                      external={true}
                      href={benefit.cta.url}
                    >
                      {benefit.cta.title && benefit.cta.title.length > 0
                        ? benefit.cta.title
                        : t('Check the benefit')}
                    </Button>
                  )}
                </div>
              </ImageAccordion>
            </li>
          ))}
        </ul>
        <h2 className='mb-8 text-xl text-center md:text-2xl'>
          {t('Upcoming benefits')}
        </h2>
        <ul>
          {upcomingBenefits &&
            upcomingBenefits.sort(sortBenefits).map((benefit, i) => (
              <li key={i}>
                <ImageAccordion
                  title={
                    <div className='flex flex-col'>
                      <h3 className='text-2xl md:mt-4'>{benefit.name}</h3>
                      {benefit.dates.end && (
                        <p
                          className='text-xl font-rukaNeue'
                          dangerouslySetInnerHTML={{
                            __html: `${t('Valid starting from')} ${formatDate(
                              benefit.dates.start
                            )} ${t('until')} ${formatDate(benefit.dates.end)}`,
                          }}
                        ></p>
                      )}
                      <p className='font-rukaNeue text-xl'>
                        {benefit.subtitle}
                      </p>
                    </div>
                  }
                  image={benefit.image}
                  isUpcomingBenefit={true}
                >
                  <div className='my-4 bg-gray-400'>
                    <h3>{benefit.title}</h3>
                    <p
                      className='my-4'
                      dangerouslySetInnerHTML={{ __html: benefit.body }}
                    ></p>
                    {benefit.cta.url && (
                      <Button
                        className='w-full mt-8 md:mx-auto md:w-fit md:px-4 md:min-w-[20rem]'
                        external={true}
                        href={benefit.cta.url}
                      >
                        {benefit.cta.title && benefit.cta.title.length > 0
                          ? benefit.cta.title
                          : t('Check the benefit')}
                      </Button>
                    )}
                  </div>
                </ImageAccordion>
              </li>
            ))}
        </ul>
      </>
    );
  } else if (errorMessage) {
    secondaryContent = <p>{errorMessage}</p>;
  }

  return (
    <div className='bookings'>
      <Page
        pageId={pageId[domain] ?? ''}
        mainContent={mainContent}
        secondaryContent={secondaryContent}
      />
    </div>
  );
}

export default BenefitsPage;
